import styled, { x } from '@xstyled/styled-components';
import React, { useEffect, useRef, useState } from 'react';
import { t } from 'ttag';
import { select } from '~/lib/select';
import { Button } from '~/ui/buttons/Button';
import { Flex } from '~/ui/flex/Flex';
import { LlamaModal } from '~/ui/llama-modal/LlamaModal';
import { Logo } from '~/ui/logo/Logo';
import { TrainingState, useTrainingStore } from '../state';
import { UpdateProfileMutationVariables } from '~/data';
import { updateProfileData } from '~/training/state/settings/updateProfileData';
import { useTrainingParams } from '~/training/useTrainingParams';
import { useRouteMatch } from 'react-router';
import { Tooltip } from '~/ui/tooltip/Tooltip';
import ReactSelect, {
  MultiValue,
  SingleValue,
  StylesConfig
} from 'react-select';
import { shouldSendInteractions, useLmsAdapter } from '~/lms';

type SelectOption = { value: string; label: string };

/** Visually same as the old <small>, but a real button for keyboard/SR. */
const SimpleTooltip = styled.button`
  background-color: #fcf8e9;
  color: #000;
  border-radius: 20px;
  padding: 0px 5px 0px 5px;
  cursor: help;
  position: absolute;
  margin-left: 5px;
  border: none;
  font-size: smaller;
  font-family: inherit;
  line-height: normal;

  &:focus-visible {
    outline: 2px solid var(--color-focus-ring);
    outline-offset: 2px;
  }
`;

const PromptWrapper = styled.div`
  .flex-block-for-model {
    width: 100%;
    max-height: 95vh;
    overflow: auto;
  }
  .modalStyleForDarkTheme {
    height: fit-content;
  }
  .click_continue {
    max-width: 340px;
    text-align: center;
  }
`;

const WrapperForTooltip = styled.span`
  .tooltipForRole {
    margin-top: -45px;
    margin-left: 162px;
    width: 260px;
    z-index: 100;
  }
`;

const FieldLabel = styled.label`
  font: inherit;
  color: inherit;
  cursor: default;
`;

const focusedControl = {
  backgroundColor: '#FFF',
  color: '#000'
} as const;

const singleSelectStyles = (
  extras: Record<string, unknown> = {}
): StylesConfig<SelectOption, false> => ({
  control: (base, state) => ({
    ...base,
    ...focusedControl,
    boxShadow: state.isFocused ? '0 0 0 2px var(--color-focus-ring)' : 'none',
    borderColor: state.isFocused ? 'var(--color-focus-ring)' : base.borderColor,
    outline: 'none',
    ...(extras.control as object)
  }),
  menu: base => ({
    ...base,
    backgroundColor: '#FFF',
    color: '#000',
    zIndex: 5,
    ...(extras.menu as object)
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isFocused ? '#f0f0f0' : '#FFF',
    color: '#000'
  }),
  menuList: base => ({
    ...base,
    ...(extras.menuList as object)
  })
});

const multiSelectStyles = (): StylesConfig<SelectOption, true> => ({
  control: (base, state) => ({
    ...base,
    ...focusedControl,
    maxWidth: '340px',
    boxShadow: state.isFocused ? '0 0 0 2px var(--color-focus-ring)' : 'none',
    borderColor: state.isFocused ? 'var(--color-focus-ring)' : base.borderColor,
    outline: 'none'
  }),
  menu: base => ({
    ...base,
    backgroundColor: '#FFF',
    color: '#000',
    zIndex: 9999,
    marginBottom: '8px'
  }),
  menuPortal: base => ({
    ...base,
    zIndex: 9999
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isFocused ? '#f0f0f0' : '#FFF',
    color: '#000'
  }),
  menuList: base => ({
    ...base,
    maxHeight: '120px',
    overflowY: 'auto'
  }),
  multiValue: base => ({
    ...base,
    backgroundColor: 'var(--color-accent-dk)',
    color: 'var(--color-on-accent)',
    borderRadius: '10px',
    border: '1px solid var(--color-accent-edge)'
  }),
  multiValueLabel: base => ({
    ...base,
    color: 'var(--color-on-accent)'
  }),
  multiValueRemove: base => ({
    ...base,
    color: 'var(--color-on-accent)',
    ':hover': {
      backgroundColor: 'transparent',
      color: 'var(--color-on-accent)'
    }
  })
});

const selector = (s: TrainingState) =>
  select(
    s,
    'firstName',
    'companyName',
    'logoUrl',
    'title',
    'promptOptions',
    'promptProfile'
  );

export const PromptProfile = () => {
  const { learnerTrainingId } = useTrainingParams();
  const { companyName, logoUrl, promptOptions, promptProfile } =
    useTrainingStore(selector);
  const { url } = useRouteMatch();
  const lms = useLmsAdapter();
  const dialogRef = useRef<HTMLDivElement>(null);
  const titleId = 'prompt-profile-title';
  const roleInputId = 'prompt-profile-role';
  const industryInputId = 'prompt-profile-industry';
  const regionInputId = 'prompt-profile-region';
  const subordinateInputId = 'prompt-profile-subordinate';
  const roleHelpId = 'prompt-profile-role-help';
  const introTextId = 'prompt-profile-intro';
  const confirmTextId = 'prompt-profile-confirm';

  const [role, setRole] = useState('');
  const [industry, setIndustry] = useState('');
  const [region, setRegion] = useState('');
  const [subordinateRegions, setSubordinateRegions] = useState<string[]>([]);
  const [confirmation, setConfirmation] = useState(false);
  const [disableSaveBtn, setDisableSaveBtn] = useState(true);
  const [showTooltip, setShowTooltip] = useState(false);

  const getValueForPromptOption = (id: string | number, optionType: string) => {
    const optionMap: { [key: string]: [number, string][] } = {
      industries: promptOptions?.industries || [],
      regions: promptOptions?.regions || [],
      subordinate_regions: promptOptions?.subordinate_regions || []
    };

    const options = optionMap[optionType];
    if (!options) {
      throw new Error(`Invalid option type: ${optionType}`);
    }

    const option = options.find(el => el[0] == id);
    return option ? option[1] : '';
  };

  useEffect(() => {
    if (!promptProfile && window && window.location) {
      window.location.href = `${url}`
        .replace('//', '/')
        .replace('form', 'onboarding');
    }
  }, [promptProfile, url]);

  useEffect(() => {
    if (
      !promptOptions ||
      !promptOptions.roles ||
      promptOptions.roles.length == 0
    ) {
      setDisableSaveBtn(false);
    }
  }, [promptOptions]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;

      if (showTooltip) {
        setShowTooltip(false);
        return;
      }

      if (confirmation) {
        setConfirmation(false);
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [confirmation, showTooltip]);

  // Focus step copy so SR announces the current screen (form intro or confirm).
  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      document
        .getElementById(confirmation ? confirmTextId : introTextId)
        ?.focus();
    });
    return () => cancelAnimationFrame(frame);
  }, [confirmation]);

  const showConfirmation = () => {
    setConfirmation(true);
  };

  const SendCmiIntegration = (
    role: string,
    industry: string,
    region: string,
    subordinateRegions: string[] | undefined
  ) => {
    if (!shouldSendInteractions() || !lms.isAvailable) {
      return;
    }

    try {
      if (role) {
        lms.recordMultipleChoiceInteraction(
          'profile-role',
          role,
          true,
          role,
          'Role: '
        );
      }

      if (industry) {
        lms.recordMultipleChoiceInteraction(
          'profile-industry',
          industry,
          true,
          industry,
          `Industry: `
        );
      }

      if (region) {
        lms.recordMultipleChoiceInteraction(
          'profile-region',
          region,
          true,
          region,
          `Location: `
        );
      }

      if (
        role === 'supervisor' &&
        subordinateRegions &&
        subordinateRegions.length > 0
      ) {
        subordinateRegions.forEach((subRegion, index) => {
          lms.recordMultipleChoiceInteraction(
            `profile-subordinate-region-${index}`,
            subRegion,
            true,
            subRegion,
            `Direct Reports' Regions: `
          );
        });
      }
    } catch (error) {
      console.error('Error in SendCmiIntegration:', error);
    }
  };

  const sendData = async () => {
    SendCmiIntegration(
      role,
      getValueForPromptOption(industry, 'industries'),
      getValueForPromptOption(region, 'regions'),
      subordinateRegions.map(region =>
        getValueForPromptOption(region, 'subordinate_regions')
      )
    );

    const dataForSending = {
      learnerTrainingUuid: learnerTrainingId,
      role: role,
      industryId: industry ? Number(industry) : undefined,
      regionId: region ? Number(region) : undefined,
      subordinateRegionIds:
        role == 'supervisor'
          ? subordinateRegions && subordinateRegions.length > 0
            ? subordinateRegions.map(Number)
            : undefined
          : undefined
    } as UpdateProfileMutationVariables;

    const learnerTraining = await updateProfileData(dataForSending);

    if (learnerTraining && window && window.location) {
      window.location.href = `${url}`
        .replace('//', '/')
        .replace(/\/t\/[^/]+\/form\/?$/, `/t/${learnerTraining.id}/onboarding`);
    }
  };

  const subordinateSummary =
    subordinateRegions.length > 0
      ? subordinateRegions
          .map(id => getValueForPromptOption(id, 'subordinate_regions'))
          .join(', ')
      : '';

  return (
    <PromptWrapper>
      <LlamaModal horizontal="center" vertical="center" allowScroll>
        <div
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          tabIndex={-1}
        >
          <Flex
            w="md"
            minHeight="md"
            flexDirection="column"
            p="xl"
            alignItems="center"
            justifyContent="space-between"
            className="flex-block-for-model"
          >
            <x.div className="logoDark">
              <Logo src={logoUrl} height={36} />
              {!logoUrl && (
                <p style={{ paddingBottom: '20px' }}>{companyName}</p>
              )}
            </x.div>
            <x.h2 id={titleId} fontFamily="llama">
              {t`Welcome!`}
            </x.h2>
            {confirmation ? (
              <div
                id={confirmTextId}
                className="click_continue"
                tabIndex={-1}
                style={{ outline: 'none' }}
              >
                <p>{t`By clicking on "Continue", I certify that the information below is correct and that I will have to retake the course if it is incorrect.`}</p>
                {role && (
                  <div>
                    {t`Role`}: {role}
                  </div>
                )}
                {industry && (
                  <div>
                    {t`Industry`}:{' '}
                    {getValueForPromptOption(industry, 'industries')}
                  </div>
                )}
                {region && (
                  <div>
                    {t`Region`}: {getValueForPromptOption(region, 'regions')}
                  </div>
                )}
                {role === 'supervisor' &&
                  subordinateRegions &&
                  subordinateRegions.length > 0 && (
                    <div>
                      {t`Subordinate Regions`}: {subordinateSummary}
                    </div>
                  )}
              </div>
            ) : (
              <div>
                <p id={introTextId} tabIndex={-1} style={{ outline: 'none' }}>
                  {t`Please answer a few questions about yourself:`}
                </p>
                <div style={{ marginTop: '12px', maxWidth: '340px' }}>
                  <x.h3>
                    <FieldLabel htmlFor={roleInputId}>
                      {t`Select Your Role`}{' '}
                    </FieldLabel>
                    {showTooltip && (
                      <WrapperForTooltip>
                        <span id={roleHelpId} role="tooltip">
                          <Tooltip
                            placement={'left'}
                            className={'tooltipForRole'}
                          >{t`You are a supervisor if you manage people and have direct reports.`}</Tooltip>
                        </span>
                      </WrapperForTooltip>
                    )}
                    <SimpleTooltip
                      type="button"
                      aria-label={t`More information about roles`}
                      aria-expanded={showTooltip}
                      aria-controls={showTooltip ? roleHelpId : undefined}
                      onMouseEnter={() => setShowTooltip(true)}
                      onMouseLeave={() => setShowTooltip(false)}
                      onFocus={() => setShowTooltip(true)}
                      onBlur={() => setShowTooltip(false)}
                    >
                      ?
                    </SimpleTooltip>
                  </x.h3>
                  <ReactSelect<SelectOption, false>
                    inputId={roleInputId}
                    name="role"
                    options={(promptOptions?.roles || []).map((l: string) => ({
                      value: l,
                      label: l
                    }))}
                    onChange={(selectedOption: SingleValue<SelectOption>) => {
                      setRole(selectedOption ? selectedOption.value : '');
                      setDisableSaveBtn(false);
                    }}
                    value={role ? { value: role, label: role } : null}
                    placeholder={t`Select`}
                    styles={singleSelectStyles()}
                  />
                </div>
                {promptOptions?.industries && (
                  <div style={{ marginTop: '12px' }}>
                    <x.h3>
                      <FieldLabel htmlFor={industryInputId}>
                        {t`Industry`}
                      </FieldLabel>
                    </x.h3>
                    <ReactSelect<SelectOption, false>
                      inputId={industryInputId}
                      name="industry"
                      options={Object.values(promptOptions?.industries).map(
                        (l: [number, string]) => ({
                          value: String(l[0]),
                          label: l[1]
                        })
                      )}
                      onChange={(selectedOption: SingleValue<SelectOption>) => {
                        setIndustry(selectedOption ? selectedOption.value : '');
                      }}
                      value={
                        industry
                          ? {
                              value: industry,
                              label: getValueForPromptOption(
                                industry,
                                'industries'
                              )
                            }
                          : null
                      }
                      placeholder={t`Select an option`}
                      styles={singleSelectStyles({
                        menuList: {
                          maxHeight: '200px',
                          overflowY: 'auto'
                        }
                      })}
                    />
                  </div>
                )}
                {promptOptions?.regions && (
                  <div style={{ marginTop: '12px' }}>
                    <x.h3>
                      <FieldLabel htmlFor={regionInputId}>
                        {t`Select Your Location`}
                      </FieldLabel>
                    </x.h3>
                    <ReactSelect<SelectOption, false>
                      inputId={regionInputId}
                      name="region"
                      options={Object.values(promptOptions?.regions).map(
                        (l: [number, string]) => ({
                          value: String(l[0]),
                          label: l[1]
                        })
                      )}
                      menuPlacement="top"
                      onChange={(selectedOption: SingleValue<SelectOption>) => {
                        setRegion(selectedOption ? selectedOption.value : '');
                      }}
                      value={
                        region
                          ? {
                              value: region,
                              label: getValueForPromptOption(region, 'regions')
                            }
                          : null
                      }
                      placeholder={t`Select an option`}
                      styles={singleSelectStyles({
                        menuList: {
                          maxHeight: '120px',
                          overflowY: 'auto'
                        }
                      })}
                    />
                  </div>
                )}
                {role === 'supervisor' &&
                  promptOptions?.subordinate_regions &&
                  promptOptions?.subordinate_regions.length > 0 && (
                    <div style={{ marginTop: '12px' }}>
                      <x.h3>
                        <FieldLabel htmlFor={subordinateInputId}>
                          {t`Select Your Direct Reports’ Regions`}
                        </FieldLabel>
                      </x.h3>
                      <ReactSelect<SelectOption, true>
                        inputId={subordinateInputId}
                        name="subordinateRegions"
                        options={promptOptions?.subordinate_regions.map(
                          (region: [number, string]) => ({
                            value: String(region[0]),
                            label: region[1]
                          })
                        )}
                        isMulti
                        menuPlacement="top"
                        onChange={(
                          selectedOptions: MultiValue<SelectOption>
                        ) => {
                          setSubordinateRegions(
                            selectedOptions
                              ? selectedOptions.map(option => option.value)
                              : []
                          );
                        }}
                        placeholder={t`Select an option`}
                        value={subordinateRegions.map(region => ({
                          value: region,
                          label: getValueForPromptOption(
                            region,
                            'subordinate_regions'
                          )
                        }))}
                        aria-label={
                          subordinateSummary
                            ? t`Select Your Direct Reports’ Regions. Selected: ${subordinateSummary}`
                            : t`Select Your Direct Reports’ Regions`
                        }
                        styles={multiSelectStyles()}
                      />
                      <span className="sr-only" aria-live="polite">
                        {subordinateSummary
                          ? t`Selected regions: ${subordinateSummary}`
                          : ''}
                      </span>
                    </div>
                  )}
              </div>
            )}
            {confirmation ? (
              <div>
                <Button
                  style={{ marginRight: '20px' }}
                  mt={'20px'}
                  onClick={() => setConfirmation(false)}
                >{t`Back`}</Button>
                <Button mt={'20px'} onClick={sendData}>{t`Continue`}</Button>
              </div>
            ) : (
              <Button
                mt={'20px'}
                onClick={showConfirmation}
                disabled={disableSaveBtn}
                aria-disabled={disableSaveBtn}
              >{t`Save`}</Button>
            )}
          </Flex>
        </div>
      </LlamaModal>
    </PromptWrapper>
  );
};
